import React, { useState } from 'react';
import { Box, TextField, IconButton, Chip } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

interface OptionInputProps {
  name: string;
  values: string[];
  onNameChange: (newName: string) => void;
  onValuesChange: (newValues: string[]) => void;
  onDelete: () => void;
}

const OptionInput: React.FC<OptionInputProps> = ({
  name,
  values,
  onNameChange,
  onValuesChange,
  onDelete,
}) => {
  const [newValue, setNewValue] = useState('');

  // Add a new value to the option
  const handleAddValue = () => {
    if (newValue.trim() && !values.includes(newValue.trim())) {
      onValuesChange([...values, newValue.trim()]);
      setNewValue('');
    }
  };

  // Delete a value from the option
  const handleDeleteValue = (value: string) => {
    onValuesChange(values.filter((v) => v !== value));
  };

  // Handle Enter key press for adding values
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddValue();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Option Name and Delete Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          label="Option Name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          fullWidth
          margin="normal"
        />
        <IconButton color="error" onClick={onDelete} sx={{ mt: 2 }}>
          <Delete />
        </IconButton>
      </Box>

      {/* Add Value Input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
        <TextField
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="Add a value"
          onKeyPress={handleKeyPress}
          fullWidth
        />
        <IconButton color="primary" onClick={handleAddValue}>
          <Add />
        </IconButton>
      </Box>

      {/* Display Values as Chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
        {values.map((value, index) => (
          <Chip
            key={index}
            label={value}
            onDelete={() => handleDeleteValue(value)}
            color="primary"
            sx={{ mb: 1 }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default OptionInput;